import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Receipt, Filter, CreditCard, TrendingUp, Download, FileText } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Receipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);  // Add loading state for skeleton
  const [reportForm, setReportForm] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    selected_clients: []
  });
  const [receiptForm, setReceiptForm] = useState({
    client_id: '',
    invoice_id: '',
    amount: '',
    receipt_type: 'service',
    receipt_method: 'cash',
    receipt_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchReceipts();
    fetchClients();
    fetchInvoices();
  }, []);
  
  const fetchReceipts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/receipts`);
      setReceipts(response.data);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const handleRecordReceipt = async (e) => {
    e.preventDefault();
    
    if (editingReceipt) {
      return handleUpdateReceipt(e);
    }
    
    if (isSubmitting) return;  // Prevent double submission
    
    try {
      setIsSubmitting(true);  // Disable button
      const receiptData = {
        ...receiptForm,
        amount: parseFloat(receiptForm.amount),
        invoice_id: receiptForm.invoice_id === 'none' || receiptForm.invoice_id === '' ? null : receiptForm.invoice_id
      };
      
      await axios.post(`${API}/receipts`, receiptData);
      
      resetForm();
      fetchReceipts();
      alert('Receipt recorded successfully!');
      
    } catch (error) {
      console.error('Error recording receipt:', error);
      alert('Error recording receipt');
    } finally {
      setIsSubmitting(false);  // Re-enable button
    }
  };

  // Fetch invoices for selected client
  const fetchClientInvoices = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/invoices`);
      setClientInvoices(response.data);
    } catch (error) {
      console.error('Error fetching client invoices:', error);
      setClientInvoices([]);
    }
  };

  const getClientInvoices = () => {
    return clientInvoices.filter(invoice => 
      invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'partial'
    );
  };

  // Handle client selection change
  const handleClientChange = (clientId) => {
    setReceiptForm({...receiptForm, client_id: clientId, invoice_id: ''});
    if (clientId) {
      fetchClientInvoices(clientId);
    } else {
      setClientInvoices([]);
    }
  };

  // Edit receipt
  const handleEditReceipt = (receipt) => {
    setEditingReceipt(receipt);
    setReceiptForm({
      client_id: receipt.client_id,
      invoice_id: receipt.invoice_id || '',
      amount: receipt.amount.toString(),
      receipt_type: receipt.receipt_type,
      receipt_method: receipt.receipt_method || 'cash',
      receipt_date: receipt.receipt_date,
      notes: receipt.notes || ''
    });
    
    // Fetch invoices for the client
    if (receipt.client_id) {
      fetchClientInvoices(receipt.client_id);
    }
    
    setShowReceiptModal(true);
  };

  // Delete receipt
  const handleDeleteReceipt = async (receiptId) => {
    if (!confirm('Are you sure you want to delete this receipt? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/receipts/${receiptId}`);
      fetchReceipts();
      alert('Receipt deleted successfully!');
    } catch (error) {
      console.error('Error deleting receipt:', error);
      alert('Error deleting receipt');
    }
  };

  // Update receipt
  const handleUpdateReceipt = async (e) => {
    e.preventDefault();
    
    try {
      const receiptData = {
        ...receiptForm,
        amount: parseFloat(receiptForm.amount),
        invoice_id: receiptForm.invoice_id === 'none' ? null : receiptForm.invoice_id
      };
      
      await axios.put(`${API}/receipts/${editingReceipt.id}`, receiptData);
      
      // Reset form
      setReceiptForm({
        client_id: '',
        invoice_id: '',
        amount: '',
        receipt_type: 'service',
        receipt_method: 'cash',
        receipt_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      setEditingReceipt(null);
      setShowReceiptModal(false);
      fetchReceipts();
      alert('Receipt updated successfully!');
      
    } catch (error) {
      console.error('Error updating receipt:', error);
      alert('Error updating receipt');
    }
  };

  // Reset form and close modal
  const resetForm = () => {
    setReceiptForm({
      client_id: '',
      invoice_id: '',
      amount: '',
      receipt_type: 'service',
      receipt_method: 'cash',
      receipt_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingReceipt(null);
    setClientInvoices([]);
    setShowReceiptModal(false);
  };

  // Export receipts report
  const handleExportReport = async () => {
    try {
      const params = new URLSearchParams({
        start_date: reportForm.start_date,
        end_date: reportForm.end_date
      });
      
      if (reportForm.selected_clients.length > 0) {
        params.append('client_ids', reportForm.selected_clients.join(','));
      }
      
      const response = await axios.get(`${API}/receipts/reports/export?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receipt_Report_${reportForm.start_date}_to_${reportForm.end_date}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowReportsModal(false);
      alert('Report exported successfully!');
      
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Error exporting report');
    }
  };
  
  const getReceiptTypeBadge = (type) => {
    return (
      <Badge variant={type === 'service' ? 'default' : 'secondary'}>
        {type === 'service' ? 'Service' : 'Tax Receipt'}
      </Badge>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                Receipt Tracking
              </h1>
              <p className="text-gray-600 text-lg">Monitor service receipts and tax receipts with comprehensive tracking</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-medium">Total Receipts</div>
                <div className="text-2xl font-bold">{receipts.length}</div>
              </div>
              <Button 
                onClick={() => setShowReportsModal(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
              >
                <FileText className="w-5 h-5 mr-2" />
                Export Reports
              </Button>
              <Button 
                onClick={() => navigate('/payment-status')}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Receipt Status
              </Button>
              <Button 
                data-testid="add-receipt-btn"
                onClick={() => {
                  setEditingReceipt(null);
                  setShowReceiptModal(true);
                }}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg"
              >
                <Plus className="w-5 h-5 mr-2" />
                Record Receipt
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        {/* Statistics Cards - Enhanced Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-green-100 text-sm font-semibold uppercase tracking-wide mb-2">Service Receipts</p>
              <div className="text-4xl font-black mb-2">{loading ? '...' : receipts.filter(p => p.receipt_type === 'service').length}</div>
              <div className="text-green-100 text-sm opacity-90">Total service fees</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-blue-100 text-sm font-semibold uppercase tracking-wide mb-2">Tax Receipts</p>
              <div className="text-4xl font-black mb-2">{loading ? '...' : receipts.filter(p => p.receipt_type === 'tax').length}</div>
              <div className="text-blue-100 text-sm opacity-90">Tax collections</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-500 to-orange-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-yellow-100 text-sm font-semibold uppercase tracking-wide mb-2">Partial Receipts</p>
              <div className="text-4xl font-black mb-2">{loading ? '...' : receipts.filter(p => p.status === 'partial').length}</div>
              <div className="text-yellow-100 text-sm opacity-90">Partially paid</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 rounded-2xl">
            <CardContent className="p-8 text-center">
              <p className="text-purple-100 text-sm font-semibold uppercase tracking-wide mb-2">Total Received</p>
              <div className="text-4xl font-black mb-2">{loading ? '...' : `₹${receipts.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}`}</div>
              <div className="text-purple-100 text-sm opacity-90">All receipts</div>
            </CardContent>
          </Card>
        </div>

        {/* Payments Table */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center">
              <TrendingUp className="w-6 h-6 mr-3" />
              Receipt Records
            </CardTitle>
            <CardDescription className="text-emerald-100">
              Comprehensive tracking of all service fees and tax receipts received
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              // Skeleton Loader
              <div className="p-8 space-y-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-12 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded flex-1 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded w-32 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded w-24 animate-pulse"></div>
                    <div className="h-12 bg-gray-200 rounded w-32 animate-pulse"></div>
                  </div>
                ))}
              </div>
            ) : receipts.length === 0 ? (
              // Empty State
              <div className="flex flex-col items-center justify-center py-24 px-4 text-center">
                <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center mx-auto mb-8">
                  <Receipt className="w-16 h-16 text-orange-600" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">No Receipts Recorded</h3>
                <p className="text-lg text-gray-600 max-w-md mb-8 leading-relaxed">
                  Keep track of all payments received from clients. Record receipts and link them to invoices for complete financial visibility.
                </p>
                <Button 
                  onClick={() => {
                    setEditingReceipt(null);
                    setShowReceiptModal(true);
                  }}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold px-10 py-6 text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 h-14"
                >
                  <Plus className="w-6 h-6 mr-2" />
                  Add First Receipt
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-orange-100">
                      <TableHead className="font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="font-semibold text-gray-700">Client</TableHead>
                      <TableHead className="font-semibold text-gray-700">Invoice</TableHead>
                      <TableHead className="font-semibold text-gray-700">Type</TableHead>
                      <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                      <TableHead className="font-semibold text-gray-700">Method</TableHead>
                      <TableHead className="font-semibold text-gray-700">Status</TableHead>
                      <TableHead className="font-semibold text-gray-700">Notes</TableHead>
                      <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receipts.map((receipt) => (
                <TableRow key={receipt.id}>
                  <TableCell>{new Date(receipt.receipt_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{receipt.client?.name || 'Unknown Client'}</div>
                      <div className="text-sm text-gray-500">{receipt.client?.organization_name || ''}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {receipt.invoice ? (
                      <Badge variant="outline">{receipt.invoice.invoice_number}</Badge>
                    ) : (
                      <span className="text-gray-400">No Invoice</span>
                    )}
                  </TableCell>
                  <TableCell>{getReceiptTypeBadge(receipt.receipt_type)}</TableCell>
                  <TableCell className="font-medium">₹{receipt.amount.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {receipt.receipt_method ? receipt.receipt_method.replace('_', ' ').toUpperCase() : 'CASH'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={receipt.status === 'received' ? 'default' : receipt.status === 'partial' ? 'secondary' : 'destructive'}>
                      {receipt.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{receipt.notes || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button
                        onClick={() => handleEditReceipt(receipt)}
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteReceipt(receipt.id)}
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
                </TableBody>
              </Table>
            </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Record Payment Modal */}
      {showReceiptModal && (
        <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                {editingReceipt ? (
                  <>
                    <Edit className="w-5 h-5 mr-2 text-indigo-600" />
                    Edit Receipt
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2 text-indigo-600" />
                    Record Receipt
                  </>
                )}
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {editingReceipt ? 'Update receipt details' : 'Record a new receipt received from client'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleRecordReceipt} className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="client_id">Select Client *</Label>
                  <Select 
                    value={receiptForm.client_id} 
                    onValueChange={handleClientChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} - {client.organization_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="invoice_id">Related Invoice (Optional)</Label>
                  <Select 
                    value={receiptForm.invoice_id} 
                    onValueChange={(value) => setReceiptForm({...receiptForm, invoice_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select invoice (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific invoice (General Payment)</SelectItem>
                      <SelectItem value="partial">Partial Payment (Not tied to specific invoice)</SelectItem>
                      {getClientInvoices().map((invoice) => (
                        <SelectItem key={invoice.id} value={invoice.id}>
                          {invoice.invoice_number} - ₹{invoice.total_amount.toLocaleString()} 
                          {invoice.status === 'partial' && ' (Partially Paid)'}
                          {invoice.status === 'overdue' && ' (Overdue)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="receipt_type">Receipt Type *</Label>
                    <Select 
                      value={receiptForm.receipt_type} 
                      onValueChange={(value) => setReceiptForm({...receiptForm, receipt_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="service">Service Fee</SelectItem>
                        <SelectItem value="tax">Tax Receipt</SelectItem>
                        <SelectItem value="advance">Advance Receipt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="receipt_method">Receipt Method *</Label>
                    <Select 
                      value={receiptForm.receipt_method} 
                      onValueChange={(value) => setReceiptForm({...receiptForm, receipt_method: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="card">Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="amount">Amount (₹) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      placeholder="Enter amount"
                      value={receiptForm.amount}
                      onChange={(e) => setReceiptForm({...receiptForm, amount: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="receipt_date">Receipt Date *</Label>
                    <Input
                      id="receipt_date"
                      type="date"
                      value={receiptForm.receipt_date}
                      onChange={(e) => setReceiptForm({...receiptForm, receipt_date: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    rows="2"
                    placeholder="Additional notes (optional)"
                    value={receiptForm.notes}
                    onChange={(e) => setReceiptForm({...receiptForm, notes: e.target.value})}
                  />
                </div>

                {/* Receipt Summary */}
                {receiptForm.amount && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                    <h4 className="font-medium text-indigo-800 mb-2">Summary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Amount:</span>
                        <span className="font-medium">₹{parseFloat(receiptForm.amount || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span className="font-medium">{receiptForm.receipt_type.replace('_', ' ').toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Method:</span>
                        <span className="font-medium">{receiptForm.receipt_method.replace('_', ' ').toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : (editingReceipt ? 'Update Receipt' : 'Record Receipt')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Reports Modal */}
      {showReportsModal && (
        <Dialog open={showReportsModal} onOpenChange={setShowReportsModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-800 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Export Receipt Reports
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Generate receipt reports with custom date filters
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={reportForm.start_date}
                    onChange={(e) => setReportForm({...reportForm, start_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={reportForm.end_date}
                    onChange={(e) => setReportForm({...reportForm, end_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Client Filter (Optional)</Label>
                <Select 
                  value={reportForm.selected_clients.join(',')} 
                  onValueChange={(value) => {
                    const clientIds = value === 'all' ? [] : [value];
                    setReportForm({...reportForm, selected_clients: clientIds});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All clients or select specific" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.organization_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">Report Details:</h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>• Date range: {reportForm.start_date} to {reportForm.end_date}</p>
                  <p>• Client filter: {reportForm.selected_clients.length === 0 ? 'All clients' : 'Selected clients'}</p>
                  <p>• Columns: Date, Client, Description, Type, Amount, Method, Notes</p>
                  <p>• Format: Excel (.xlsx)</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowReportsModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleExportReport}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Receipts;
